import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  X, 
  CheckCircle, 
  AlertCircle, 
  Info, 
  MessageSquare,
  Car,
  Users,
  Clock,
  MapPin
} from 'lucide-react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  IconButton,
  Badge,
  Chip,
  Divider,
  Button,
  Fade,
  Slide,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: 400,
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
  }
}));

const NotificationItem = styled(Paper)(({ theme, type }) => ({
  margin: theme.spacing(1),
  padding: theme.spacing(2),
  borderRadius: 12,
  border: '1px solid',
  borderColor: type === 'success' ? 'rgba(16, 185, 129, 0.2)' :
                type === 'error' ? 'rgba(239, 68, 68, 0.2)' :
                type === 'warning' ? 'rgba(245, 158, 11, 0.2)' :
                'rgba(99, 102, 241, 0.2)',
  background: type === 'success' ? 'rgba(16, 185, 129, 0.05)' :
              type === 'error' ? 'rgba(239, 68, 68, 0.05)' :
              type === 'warning' ? 'rgba(245, 158, 11, 0.05)' :
              'rgba(99, 102, 241, 0.05)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
  }
}));

const NotificationCenter = ({ open, onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Simulate notifications
  useEffect(() => {
    const mockNotifications = [
      {
        id: 1,
        type: 'success',
        title: 'Ride Connected!',
        message: 'You have successfully connected with John for your ride to work.',
        time: '2 minutes ago',
        read: false,
        icon: Car
      },
      {
        id: 2,
        type: 'info',
        title: 'New Connect Request',
        message: 'Sarah wants to connect for your ride to the airport.',
        time: '5 minutes ago',
        read: false,
        icon: Users
      },
      {
        id: 3,
        type: 'warning',
        title: 'Ride Reminder',
        message: 'Your scheduled ride starts in 15 minutes.',
        time: '10 minutes ago',
        read: true,
        icon: Clock
      },
      {
        id: 4,
        type: 'error',
        title: 'Payment Failed',
        message: 'Your last payment was unsuccessful. Please update your payment method.',
        time: '1 hour ago',
        read: true,
        icon: AlertCircle
      },
      {
        id: 5,
        type: 'success',
        title: 'Feedback Received',
        message: 'Thank you for your feedback! Your rating helps improve our service.',
        time: '2 hours ago',
        read: true,
        icon: MessageSquare
      }
    ];

    setNotifications(mockNotifications);
    setUnreadCount(mockNotifications.filter(n => !n.read).length);
  }, []);

  const markAsRead = (notificationId) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId ? { ...notif, read: true } : notif
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
    setUnreadCount(0);
  };

  const deleteNotification = (notificationId) => {
    setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
  };

  const getIconColor = (type) => {
    switch (type) {
      case 'success': return '#10b981';
      case 'error': return '#ef4444';
      case 'warning': return '#f59e0b';
      case 'info': return '#6366f1';
      default: return '#6366f1';
    }
  };

  const getIcon = (type, customIcon) => {
    if (customIcon) {
      const IconComponent = customIcon;
      return <IconComponent size={20} />;
    }
    
    switch (type) {
      case 'success': return <CheckCircle size={20} />;
      case 'error': return <AlertCircle size={20} />;
      case 'warning': return <AlertCircle size={20} />;
      case 'info': return <Info size={20} />;
      default: return <Info size={20} />;
    }
  };

  return (
    <StyledDrawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 400 } }
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {unreadCount > 0 && (
              <Chip 
                label={unreadCount} 
                size="small" 
                color="primary"
                sx={{ fontWeight: 600 }}
              />
            )}
            <IconButton onClick={onClose} size="small">
              <X size={20} />
            </IconButton>
          </Box>
        </Box>
        
        {unreadCount > 0 && (
          <Button 
            variant="text" 
            size="small" 
            onClick={markAllAsRead}
            sx={{ color: '#6366f1', fontWeight: 600 }}
          >
            Mark all as read
          </Button>
        )}
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Bell size={48} color="#9ca3af" />
            <Typography variant="body1" sx={{ color: '#6b7280', mt: 2 }}>
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {notifications.map((notification, index) => (
              <Slide direction="left" in timeout={300 + index * 100} key={notification.id}>
                <ListItem sx={{ p: 0 }}>
                  <NotificationItem type={notification.type} sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ 
                        color: getIconColor(notification.type),
                        mt: 0.5
                      }}>
                        {getIcon(notification.type, notification.icon)}
                      </Box>
                      
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {notification.title}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>
                            {notification.time}
                          </Typography>
                        </Box>
                        
                        <Typography variant="body2" sx={{ color: '#374151', mb: 2 }}>
                          {notification.message}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {!notification.read && (
                            <Chip 
                              label="New" 
                              size="small" 
                              color="primary"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                          
                          <Box sx={{ flex: 1 }} />
                          
                          {!notification.read && (
                            <Button 
                              variant="text" 
                              size="small" 
                              onClick={() => markAsRead(notification.id)}
                              sx={{ color: '#6366f1', fontSize: '0.75rem' }}
                            >
                              Mark read
                            </Button>
                          )}
                          
                          <IconButton 
                            size="small" 
                            onClick={() => deleteNotification(notification.id)}
                            sx={{ color: '#9ca3af' }}
                          >
                            <X size={16} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  </NotificationItem>
                </ListItem>
              </Slide>
            ))}
          </List>
        )}
      </Box>
    </StyledDrawer>
  );
};

export default NotificationCenter; 